import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { API } from "../lib/api";

function fmt(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

async function J(url, opts = {}, key) {
  const r = await fetch(`${API}${url}`, { ...opts, headers: { "X-Admin-Key": key, "Content-Type": "application/json", ...(opts.headers || {}) } });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
}

const POLL_MS = 8000;

export default function AiVisibilityTab({ adminKey }) {
  const [data, setData] = useState(null);
  const [queries, setQueries] = useState([]);
  const [newQuery, setNewQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [triggering, setTriggering] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [d, q] = await Promise.all([
        J("/admin/ai-visibility", {}, adminKey),
        J("/admin/ai-visibility/queries", {}, adminKey),
      ]);
      setData(d);
      setQueries(q.queries || []);
    } catch (e) { toast.error(e.message); }
  }, [adminKey]);

  useEffect(() => { setLoading(true); load().finally(() => setLoading(false)); }, [load]);

  // Poll while a run is in progress
  useEffect(() => {
    if (data?.running && !pollRef.current) {
      pollRef.current = setInterval(load, POLL_MS);
    }
    if (!data?.running && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
    return () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [data?.running, load]);

  const runNow = async () => {
    setTriggering(true);
    try {
      const res = await J("/admin/ai-visibility/run", { method: "POST" }, adminKey);
      if (res.already_running) toast("A run is already in progress");
      else toast.success("Run queued · takes ~90s");
      load();
    } catch (e) { toast.error(e.message); }
    finally { setTriggering(false); }
  };

  const addQuery = async (e) => {
    e.preventDefault();
    if (!newQuery.trim()) return;
    try {
      await J("/admin/ai-visibility/queries", { method: "POST", body: JSON.stringify({ prompt: newQuery.trim() }) }, adminKey);
      setNewQuery("");
      toast.success("Query added");
      load();
    } catch (e) { toast.error(e.message); }
  };

  const delQuery = async (id) => {
    if (!confirm("Delete this tracked query?")) return;
    try { await J(`/admin/ai-visibility/queries/${id}`, { method: "DELETE" }, adminKey); load(); }
    catch (e) { toast.error(e.message); }
  };

  const latest = data?.latest;
  const mentionRate = data?.mention_rate_by_model || [];
  const topCompetitors = data?.top_competitors || [];
  const groupedResults = latest?.results?.reduce((acc, r) => {
    (acc[r.prompt] = acc[r.prompt] || []).push(r);
    return acc;
  }, {}) || {};

  const noRunYet = !latest?.finished_at;

  return (
    <div className="mt-8" data-testid="aiv-tab">
      {/* Top bar */}
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <div className="overline mb-1">LLM visibility</div>
          <h2 className="font-display text-2xl leading-tight">How AI assistants answer for your brand</h2>
          <p className="text-sm text-rw-ink2 mt-2 max-w-2xl">
            Every day at 04:00 UTC (09:30 IST) we ask 3 LLMs (Claude Sonnet 4.6, GPT-5.4, Gemini 3.1 Pro) your tracked queries
            and log whether they mention <em>RIZ N WASH</em> — plus who they recommend instead.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {data?.running && (
            <div className="flex items-center gap-2 text-xs text-rw-ink2" data-testid="aiv-running-badge">
              <span className="w-2 h-2 rounded-full bg-rw-blue animate-pulse" />
              Running · started {fmt(data.running.started_at)}
            </div>
          )}
          <motion.button
            whileHover={{ y: -2 }} whileTap={{ scale: 0.97 }}
            onClick={runNow}
            disabled={triggering || !!data?.running}
            className="btn-dark rounded-full px-5 py-2.5 text-sm font-semibold disabled:opacity-50"
            data-testid="aiv-run-btn"
          >
            {data?.running ? "Running…" : triggering ? "Queuing…" : "Run now →"}
          </motion.button>
        </div>
      </div>

      {/* Summary */}
      {loading && !data && <div className="text-rw-ink3 text-sm">Loading…</div>}
      {data && (
        <div className="grid grid-cols-2 md:grid-cols-4 border border-rw-ink/10 rounded-2xl overflow-hidden bg-rw-bg2" data-testid="aiv-summary">
          <div className="p-5 border-r border-b md:border-b-0 border-rw-ink/10">
            <div className="overline">Latest run</div>
            <div className="font-display text-lg mt-1">{latest ? fmt(latest.finished_at || latest.started_at) : "Never"}</div>
          </div>
          <div className="p-5 border-b md:border-b-0 md:border-r border-rw-ink/10">
            <div className="overline">Brand mentions</div>
            <div className="font-display text-2xl mt-1 tabular-nums">
              {latest?.mention_count ?? 0}
              <span className="text-base text-rw-ink3"> / {latest?.result_count ?? 0}</span>
            </div>
          </div>
          <div className="p-5 border-r border-rw-ink/10">
            <div className="overline">Queries tracked</div>
            <div className="font-display text-2xl mt-1 tabular-nums">{queries.length}</div>
          </div>
          <div className="p-5">
            <div className="overline">Models</div>
            <div className="font-display text-2xl mt-1 tabular-nums">{mentionRate.length || 3}</div>
          </div>
        </div>
      )}

      {noRunYet && !data?.running && (
        <div className="mt-8 text-center text-rw-ink3 py-16 border border-dashed border-rw-ink/15 rounded-2xl">
          <div className="font-display text-xl text-rw-ink mb-2">No runs yet</div>
          <div className="text-sm">Hit "Run now →" to sample the LLMs today.</div>
        </div>
      )}

      {/* Mention rate per model + competitors */}
      {latest?.finished_at && (
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid="aiv-model-rates">
            <div className="overline mb-3">Mention rate · by model</div>
            <ul className="space-y-4">
              {mentionRate.map((m) => (
                <li key={m.model} data-testid={`aiv-model-${m.model}`}>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="font-medium flex-1 truncate">{m.model}</span>
                    <span className="text-xs text-rw-ink3">{m.source}</span>
                    <span className="text-sm font-semibold tabular-nums w-14 text-right">{Math.round(m.rate * 100)}%</span>
                    <span className="text-xs text-rw-ink3 tabular-nums w-12 text-right">{m.mentioned}/{m.total}</span>
                  </div>
                  <div className="h-2 rounded-full bg-rw-bg3 overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }} animate={{ width: `${Math.max(3, m.rate * 100)}%` }}
                      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                      className="h-full rounded-full"
                      style={{ background: m.rate > 0 ? "#92B4D2" : "#E28A71" }}
                    />
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid="aiv-competitors">
            <div className="overline mb-3">Who LLMs recommend instead</div>
            {topCompetitors.length === 0 && <p className="text-sm text-rw-ink3">No competitor names detected in this run.</p>}
            <ul className="space-y-2">
              {topCompetitors.map((c, i) => (
                <li key={c.name} className="flex items-baseline gap-3 py-1" data-testid={`aiv-comp-${i}`}>
                  <span className="text-rw-ink3 tabular-nums w-6 text-right">{i + 1}</span>
                  <span className="flex-1 font-medium truncate">{c.name}</span>
                  <span className="text-xs text-rw-ink3">mentioned {c.mentions}× this run</span>
                </li>
              ))}
            </ul>
            <p className="text-xs text-rw-ink3 mt-4">
              These are the businesses LLMs currently associate with your keywords. Getting them to mention you means citations in press, forum threads, Wikipedia, business directories, and third-party review lists.
            </p>
          </div>
        </div>
      )}

      {/* Per-query breakdown */}
      {latest?.finished_at && (
        <div className="mt-8 rounded-2xl border border-rw-ink/10 overflow-hidden bg-rw-bg2" data-testid="aiv-per-query">
          <div className="px-6 py-4 border-b border-rw-ink/10">
            <div className="overline">Per-query breakdown · latest run</div>
          </div>
          <ul className="divide-y divide-rw-ink/10">
            {Object.entries(groupedResults).map(([prompt, results]) => {
              const mentions = results.filter((r) => r.mentioned).length;
              const isOpen = expanded === prompt;
              return (
                <li key={prompt}>
                  <button
                    onClick={() => setExpanded(isOpen ? null : prompt)}
                    className="w-full flex items-center gap-3 px-6 py-4 text-left hover:bg-rw-bg3/50"
                    data-testid="aiv-query-row"
                  >
                    <span className="flex-1 truncate">{prompt}</span>
                    <span className={`text-xs font-mono px-2 py-1 rounded-full ${mentions > 0 ? "bg-green-100 text-green-800" : "bg-red-50 text-red-700"}`}>
                      {mentions}/{results.length}
                    </span>
                    <span className="text-rw-ink3 text-xs">{isOpen ? "▲" : "▼"}</span>
                  </button>
                  {isOpen && (
                    <div className="px-6 pb-6 space-y-3 bg-rw-bg3/40">
                      {results.map((r, i) => (
                        <div key={i} className="border border-rw-ink/10 rounded-xl p-4 bg-rw-bg2" data-testid={`aiv-answer-${r.model_id}`}>
                          <div className="flex items-baseline gap-2 mb-2">
                            <span className="font-semibold text-sm">{r.model_label}</span>
                            <span className={`text-xs font-mono px-2 py-0.5 rounded-full ${r.mentioned ? "bg-green-100 text-green-800" : "bg-red-50 text-red-700"}`}>
                              {r.mentioned ? "MENTIONED" : "not mentioned"}
                            </span>
                            {r.competitors_mentioned?.length > 0 && (
                              <span className="text-xs text-rw-ink3">
                                also mentioned: {r.competitors_mentioned.join(", ")}
                              </span>
                            )}
                            <span className="ml-auto text-xs text-rw-ink3">{r.latency_ms}ms</span>
                          </div>
                          {r.error ? (
                            <div className="text-xs text-red-700">Error: {r.error}</div>
                          ) : (
                            <p className="text-sm text-rw-ink2 whitespace-pre-wrap max-h-64 overflow-y-auto">{r.answer || "—"}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Query CRUD */}
      <div className="mt-8 rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6" data-testid="aiv-queries">
        <div className="overline mb-3">Tracked queries</div>
        <form onSubmit={addQuery} className="flex flex-wrap gap-2 mb-4">
          <input
            type="text"
            value={newQuery}
            onChange={(e) => setNewQuery(e.target.value)}
            placeholder="e.g. Best laundry for silk sarees in Gurugram?"
            maxLength={300}
            className="flex-1 min-w-[240px] rounded-full border border-rw-ink/15 px-4 py-2.5 text-sm bg-white focus:outline-none focus:border-rw-blue"
            data-testid="aiv-add-input"
          />
          <button type="submit" className="btn-dark rounded-full px-5 py-2.5 text-sm font-semibold" data-testid="aiv-add-btn">
            Add query
          </button>
        </form>
        <ul className="divide-y divide-rw-ink/10">
          {queries.map((q) => (
            <li key={q.id} className="flex items-center gap-3 py-3" data-testid={`aiv-query-${q.id}`}>
              <span className="flex-1 text-sm">{q.prompt}</span>
              <button onClick={() => delQuery(q.id)} className="text-xs text-rw-ink3 hover:text-red-600 font-semibold">
                Remove
              </button>
            </li>
          ))}
        </ul>
      </div>

      {/* Run history */}
      {data?.runs?.length > 1 && (
        <div className="mt-8">
          <div className="overline mb-3">Recent runs</div>
          <ul className="divide-y divide-rw-ink/10 border border-rw-ink/10 rounded-2xl overflow-hidden bg-rw-bg2">
            {data.runs.map((r) => (
              <li key={r.id} className="flex items-center gap-3 px-5 py-3 text-sm" data-testid={`aiv-run-${r.id}`}>
                <span className="flex-1">
                  {fmt(r.started_at)}
                  {!r.finished_at && <span className="ml-2 text-xs text-rw-ink3">· in progress</span>}
                </span>
                <span className="text-rw-ink3 tabular-nums">{r.mention_count}/{r.result_count} mentions</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
